(() => {
  'use strict';

  const ROOT_SELECTOR = '.nut-pdp-section';

  const money = (minor, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EGP',
        maximumFractionDigits: 2
      }).format((Number(minor) || 0) / 100);
    } catch (error) {
      return `${currency || 'EGP'} ${Math.round((Number(minor) || 0) / 100)}`;
    }
  };

  class NutPdpController {
    constructor(root) {
      this.root = root;
      this.info = root.querySelector('.nut-pdp-info');
      this.gallery = root.querySelector('.nut-pdp-gallery');
      this.variants = this.readVariants();
      this.variantMediaMap = this.readVariantMediaMap();
      this.currentVariant = this.getInitialVariant();
      this.pack = root.querySelector('[data-nut-pdp-pack]');
      this.quantityInput = root.querySelector('[data-nut-pdp-quantity]');
      this.shipping = root.querySelector('[data-nut-pdp-shipping]');
      this.currency = this.pack?.dataset.currency || this.shipping?.dataset.currency || 'EGP';
      this.locale = document.documentElement.lang || 'en';
      this.bindSkinMatcher();
      this.bindPackSelector();
      this.bindVariantChanges();
      this.renderCommerce();
    }

    readVariants() {
      const script = this.info?.querySelector('[data-nut-pdp-product-json]');
      if (!script) return [];
      try { return JSON.parse(script.textContent || '[]'); }
      catch (error) { return []; }
    }

    readVariantMediaMap() {
      const script = this.gallery?.querySelector('[data-nut-pdp-variant-media-map]');
      if (!script) return new Map();

      try {
        const rows = JSON.parse(script.textContent || '[]');
        return new Map(
          rows
            .filter((row) => row && row.id)
            .map((row) => [String(row.id), Number(row.media_id || 0)])
        );
      } catch (error) {
        return new Map();
      }
    }

    getInitialVariant() {
      const formId = this.root.querySelector('hdt-variant-picker')?.getAttribute('form');
      const form = formId ? document.getElementById(formId) : this.root.querySelector('form[action*="/cart/add"]');
      const id = Number(form?.querySelector('[name="id"]')?.value || 0);
      return this.variants.find((variant) => Number(variant.id) === id) || this.variants.find((variant) => variant.available) || this.variants[0] || null;
    }

    bindSkinMatcher() {
      this.root.querySelectorAll('[data-nut-pdp-skin]').forEach((skin) => {
        skin.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-nut-pdp-skin-trigger]');
          if (!trigger) return;
          const key = trigger.dataset.nutPdpSkinTrigger;
          skin.querySelectorAll('[data-nut-pdp-skin-trigger]').forEach((button) => {
            button.setAttribute('aria-pressed', button === trigger ? 'true' : 'false');
          });
          skin.querySelectorAll('[data-nut-pdp-skin-panel]').forEach((panel) => {
            panel.hidden = panel.dataset.nutPdpSkinPanel !== key;
          });
        });
      });
    }

    bindPackSelector() {
      if (!this.pack) return;
      this.pack.addEventListener('click', (event) => {
        const option = event.target.closest('[data-nut-pdp-pack-option]');
        if (!option) return;
        this.pack.querySelectorAll('[data-nut-pdp-pack-option]').forEach((item) => {
          item.setAttribute('aria-checked', item === option ? 'true' : 'false');
        });
        this.syncQuantity();
        this.renderCommerce();
      });
      this.syncQuantity();
    }

    bindVariantChanges() {
      this.root.addEventListener('variant:change', (event) => {
        if (!event.detail?.variant) return;
        this.currentVariant = event.detail.variant;
        this.renderCommerce();
      });
      document.addEventListener('variant:change', (event) => {
        if (!this.root.contains(event.target) || !event.detail?.variant) return;
        this.currentVariant = event.detail.variant;
        this.renderCommerce();
      });
    }

    getCanonicalVariant(variant) {
      if (!variant) return null;
      return this.variants.find((item) => String(item.id) === String(variant.id)) || variant;
    }

    getVariantSize(variant) {
      variant = this.getCanonicalVariant(variant);
      if (!variant) return null;

      const candidates = [];
      if (Array.isArray(variant.options)) candidates.push(...variant.options);
      ['option1', 'option2', 'option3', 'title'].forEach((key) => {
        if (variant[key]) candidates.push(variant[key]);
      });

      for (const candidate of candidates) {
        const raw = String(candidate || '').trim();
        if (!raw) continue;

        const normalized = raw
          .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
          .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
          .replace(/٫/g, '.')
          .replace(/,/g, '.');

        /*
         * Do not use \b after Arabic units (مل / جم). JavaScript word
         * boundaries are ASCII-oriented, so values such as "125 مل" can
         * fail even though the same value works as "125 ML".
         */
        const match = normalized.match(
          /(\d+(?:\.\d+)?)\s*(ml|مل|milliliter|millilitre|milliliters|millilitres|g|gm|gr|gram|grams|جم)(?=$|[\s./,_\-–—])/i
        );

        if (!match) continue;

        const amount = Number(match[1]);
        if (!Number.isFinite(amount) || amount <= 0) continue;

        const unitRaw = String(match[2]).toLowerCase();
        const unit = /^(ml|مل|milliliter|millilitre|milliliters|millilitres)$/i.test(unitRaw)
          ? 'ml'
          : 'g';

        return { amount, unit, raw };
      }

      return null;
    }

    isMiniVariant(variant) {
      const currentSize = this.getVariantSize(variant);
      if (!currentSize) return false;

      const comparableSizes = this.variants
        .map((item) => this.getVariantSize(item))
        .filter((size) => size && size.unit === currentSize.unit)
        .map((size) => size.amount);

      const uniqueSizes = [...new Set(comparableSizes)];
      if (uniqueSizes.length < 2) return false;

      return currentSize.amount === Math.min(...uniqueSizes);
    }

    formatMiniSize(size) {
      if (!size) return '';
      const isArabic =
        (document.documentElement.lang || '').toLowerCase().startsWith('ar') ||
        document.documentElement.dir === 'rtl';
      const amount = Number.isInteger(size.amount)
        ? String(size.amount)
        : String(size.amount).replace(/\.0+$/, '');

      if (size.unit === 'ml') return `${amount}${isArabic ? 'مل' : 'ml'}`;
      if (size.unit === 'g') return `${amount}${isArabic ? 'جم' : 'g'}`;
      return size.raw || amount;
    }

    renderMiniBadge() {
      const badge = this.root.querySelector('[data-nut-mini-badge]');
      const textNode = badge?.querySelector('[data-nut-mini-badge-text]');
      if (!badge || !textNode || !this.gallery) return;

      const previousMedia = badge.closest('[data-media-id]');
      previousMedia?.classList.remove('nut-pdp-mini-media');

      const variant = this.getCanonicalVariant(this.currentVariant);
      const size = this.getVariantSize(variant);
      const isMini = Boolean(size && this.isMiniVariant(variant));
      const mediaId = variant?.id
        ? Number(this.variantMediaMap.get(String(variant.id)) || 0)
        : 0;
      const mediaNode = mediaId
        ? this.gallery.querySelector(`[data-media-id="${mediaId}"]`)
        : null;
      const show = Boolean(isMini && mediaNode);

      badge.hidden = !show;
      badge.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (!show) {
        if (badge.parentElement !== this.gallery) this.gallery.prepend(badge);
        return;
      }

      const isArabic =
        (document.documentElement.lang || '').toLowerCase().startsWith('ar') ||
        document.documentElement.dir === 'rtl';
      const sizeLabel = this.formatMiniSize(size);

      badge.setAttribute('dir', isArabic ? 'rtl' : 'ltr');
      textNode.textContent = isArabic
        ? `${sizeLabel} - حجم صغير`
        : `${sizeLabel} - mini`;

      mediaNode.classList.add('nut-pdp-mini-media');
      mediaNode.appendChild(badge);
    }

    selectedPack() {
      return this.pack?.querySelector('[data-nut-pdp-pack-option][aria-checked="true"]') || this.pack?.querySelector('[data-nut-pdp-pack-option]') || null;
    }

    syncQuantity() {
      if (!this.quantityInput) return;
      const quantity = Math.max(1, Number(this.selectedPack()?.dataset.quantity || 1));
      this.quantityInput.value = String(quantity);
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    commerceState() {
      const pack = this.selectedPack();
      const quantity = Math.max(1, Number(pack?.dataset.quantity || 1));
      const discount = Math.min(100, Math.max(0, Number(pack?.dataset.discount || 0)));
      const basePrice = Number(this.currentVariant?.price || 0);
      const regular = basePrice * quantity;
      const final = Math.round(regular * (100 - discount) / 100);
      return { pack, quantity, discount, regular, final };
    }

    renderCommerce() {
      const state = this.commerceState();
      if (this.pack) {
        this.pack.querySelectorAll('[data-nut-pdp-pack-option]').forEach((option) => {
          const quantity = Math.max(1, Number(option.dataset.quantity || 1));
          const discount = Math.min(100, Math.max(0, Number(option.dataset.discount || 0)));
          const regular = Number(this.currentVariant?.price || 0) * quantity;
          const final = Math.round(regular * (100 - discount) / 100);
          const compare = option.querySelector('[data-nut-pdp-pack-compare]');
          const finalNode = option.querySelector('[data-nut-pdp-pack-final]');
          if (compare) compare.textContent = money(regular, this.currency, this.locale);
          if (finalNode) finalNode.textContent = money(final, this.currency, this.locale);
        });
      }
      this.renderShipping(state.final);
      this.renderMiniBadge();
    }

    renderShipping(total) {
      if (!this.shipping) return;
      const threshold = Math.max(0, Number(this.shipping.dataset.threshold || 0));
      const fill = this.shipping.querySelector('[data-nut-pdp-shipping-fill]');
      const message = this.shipping.querySelector('[data-nut-pdp-shipping-message]');
      const progress = threshold > 0 ? Math.min(100, total / threshold * 100) : 0;
      if (fill) fill.style.width = `${progress}%`;
      if (!message || threshold <= 0) return;
      const remaining = Math.max(0, threshold - total);
      if (remaining === 0) {
        message.innerHTML = `<strong>${message.dataset.unlocked || this.root.querySelector('.nut-pdp-main')?.dataset.shippingUnlocked || 'Free delivery unlocked'}</strong>`;
      } else {
        const template = this.root.querySelector('.nut-pdp-main')?.dataset.shippingRemaining || 'Add [amount] for free delivery';
        message.textContent = template.replace('[amount]', money(remaining, this.currency, this.locale));
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(`${ROOT_SELECTOR}:not([data-nut-pdp-ready])`).forEach((root) => {
      root.dataset.nutPdpReady = 'true';
      new NutPdpController(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
